import React, { useMemo, useEffect, useState } from "react";
import { Text, VStack } from "@chakra-ui/react";
import { useFormContext } from "react-hook-form";
import { AdCreativeFormData } from "../../forms/adcreative";
import { FacebookPage } from "../../types";
import FacebookFeedPreview from "./FacebookFeedPreview";
import FacebookStoryPreview from "./FacebookStoryPreview";
import { analyzePlacementRequirements } from "src/utils/aspectRatio";
import { mapFacebookPlacementToAspectKey } from "src/utils/aspectRatio/placementMap";

interface FacebookPreviewProps {
  pages?: FacebookPage[];
  name: string;
  description: string;
}

const formatsToPreview = {
  square: [FacebookFeedPreview],
  portrait: [FacebookStoryPreview],
};
const formatToLabel = {
  square: "Feed",
  portrait: "Story",
};

const FacebookPreview: React.FC<FacebookPreviewProps> = ({
  pages = [],
  name,
  description,
}) => {
  const { watch } = useFormContext<AdCreativeFormData>();

  // Get form data
  const message = watch("message") || "";
  const callToAction = watch("call_to_action");
  const page = watch("page_id");
  const images = watch("images");
  const videos = watch("videos");
  const mediaType = watch("media_type");
  const websiteUrl = watch("website_url");

  const facebookPlacements = watch("facebook_placements") || [];
  const placements = useMemo(
    () => facebookPlacements.map(mapFacebookPlacementToAspectKey),
    [facebookPlacements.join(",")]
  );
  const [formats, setFormats] = useState<("square" | "portrait")[]>([
    "square",
    "portrait",
  ]);

  useEffect(() => {
    const analysis = analyzePlacementRequirements(placements);

    if (!placements.length) {
      setFormats(["square", "portrait"]);
      return;
    }

    setFormats([
      ...(analysis.needsSquare ? ["square"] : []),
      ...(analysis.needsPortrait ? ["portrait"] : []),
    ] as ("square" | "portrait")[]);
  }, [placements]);

  // Get media for display - handle carousel vs single media types
  const squareImages = images?.square || [];
  const portraitImages = images?.portrait || [];
  const squareVideos = videos?.square || [];
  const portraitVideos = videos?.portrait || [];

  // Collect media URLs for each format
  const squareMediaUrls = [
    ...squareImages.map((img) => img.picture_url),
    ...squareVideos.map((vid) => vid.source),
  ].filter(Boolean);

  const portraitMediaUrls = [
    ...portraitImages.map((img) => img.picture_url),
    ...portraitVideos.map((vid) => vid.source),
  ].filter(Boolean);

  // Get page name from page selection
  const pageName = page?.label || "Facebook Page";
  const ctaText = callToAction?.label || "Learn More";

  const mediaUrls = {
    portrait: portraitMediaUrls,
    square: mediaType === "carousel" ? portraitMediaUrls : squareMediaUrls,
  };

  return (
    <VStack
      bg="gray.400"
      p={4}
      borderRadius="lg"
      display="flex"
      justifyContent="center"
      alignItems="center"
      h="full"
      w="full"
      gap={8}
    >
      {formats.map((format) =>
        formatsToPreview[format].map((Preview) => (
          <VStack key={format} w="full">
            <Text fontWeight={"bold"} color="gray.700">
              {formatToLabel[format]}
            </Text>
            <Preview
              message={message}
              ctaText={ctaText}
              eventName={name}
              description={description}
              mediaUrls={mediaUrls[format]}
              mediaType={mediaType as "single" | "carousel"}
              pageName={pageName}
              websiteUrl={websiteUrl}
            />
          </VStack>
        ))
      )}
    </VStack>
  );
};

export default FacebookPreview;
